/**
 * Admin Login Security Utilities
 * Implements multiple layers of security for admin access
 */

const FAILED_ATTEMPTS_KEY = 'admin_login_failed_attempts';
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const MAX_FAILED_ATTEMPTS = 5;

/**
 * Rate limiting - Track failed login attempts
 */
export const checkRateLimit = () => {
  const attempts = JSON.parse(localStorage.getItem(FAILED_ATTEMPTS_KEY) || '[]');
  const now = Date.now();
  
  // Remove attempts older than lockout duration
  const recentAttempts = attempts.filter(timestamp => now - timestamp < LOCKOUT_DURATION);
  
  if (recentAttempts.length >= MAX_FAILED_ATTEMPTS) {
    const oldestAttempt = recentAttempts[0];
    const timeRemaining = LOCKOUT_DURATION - (now - oldestAttempt);
    const minutesRemaining = Math.ceil(timeRemaining / 60000);
    
    return {
      blocked: true,
      minutesRemaining
    };
  }
  
  return { blocked: false };
};

/**
 * Record a failed login attempt
 */
export const recordFailedAttempt = () => {
  const attempts = JSON.parse(localStorage.getItem(FAILED_ATTEMPTS_KEY) || '[]');
  attempts.push(Date.now());
  localStorage.setItem(FAILED_ATTEMPTS_KEY, JSON.stringify(attempts));
};

/**
 * Clear failed attempts on successful login
 */
export const clearFailedAttempts = () => {
  localStorage.removeItem(FAILED_ATTEMPTS_KEY);
};

/**
 * Generate a random admin access URL with secret parameter
 */
export const generateAdminUrl = () => {
  const randomToken = generateRandomToken(32);
  return `/admin-access/${randomToken}`;
};

/**
 * Validate admin access URL
 */
export const validateAdminUrl = (pathname) => {
  // Check if URL matches admin access pattern
  const adminPattern = /^\/admin-access\/[a-zA-Z0-9]{32}$/;
  return adminPattern.test(pathname);
};

/**
 * Extract token from admin URL
 */
export const extractAdminToken = (pathname) => {
  const match = pathname.match(/^\/admin-access\/([a-zA-Z0-9]{32})$/);
  return match ? match[1] : null;
};

/**
 * Generate random token
 */
const generateRandomToken = (length) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < length; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

/**
 * Check if user agent is suspicious
 */
export const checkSuspiciousActivity = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for common bot/scraper user agents
  const suspiciousPatterns = [
    'bot', 'crawler', 'spider', 'scraper', 'curl', 'wget', 
    'python', 'java', 'php', 'ruby', 'perl'
  ];
  
  return suspiciousPatterns.some(pattern => userAgent.includes(pattern));
};

/**
 * Detect if environment seems automated
 */
export const detectAutomation = () => {
  // Check for common automation indicators
  const checks = {
    hasChromeDevTools: window.chrome && window.chrome.runtime,
    hasSelenium: window.navigator.webdriver,
    hasPhantomJS: window.callPhantom,
    hasNightmare: window.__nightmare,
    hasPuppeteer: window.__puppeteer
  };
  
  return Object.values(checks).some(check => check === true);
};

/**
 * Log security event (for monitoring)
 */
export const logSecurityEvent = (eventType, details) => {
  const log = {
    timestamp: Date.now(),
    event: eventType,
    details,
    userAgent: navigator.userAgent,
    ip: 'client-side' // Will be captured by backend
  };
  
  // In production, send to security monitoring service
  console.warn('Security Event:', log);
  
  // Store locally for review
  const logs = JSON.parse(localStorage.getItem('admin_security_logs') || '[]');
  logs.push(log);
  
  // Keep only last 50 events
  if (logs.length > 50) {
    logs.shift();
  }
  
  localStorage.setItem('admin_security_logs', JSON.stringify(logs));
};

/**
 * Sanitize input for XSS prevention
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Get client fingerprint for tracking
 */
export const getClientFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Fingerprint', 2, 2);
  
  return {
    canvas: canvas.toDataURL(),
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform
  };
};

